import React, { useRef, useState, useEffect } from 'react';
import { Card, Button, Input, Avatar, AvatarFallback, AvatarImage, Switch } from '../ui';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { Lock, Check, Trash2, AlertCircle, Camera } from 'lucide-react';
import { uploadProfilePhoto, updateDashboardProfile, deleteProfilePhoto, uploadVerificationDocument } from '../services/lawyerService';
import { changePassword } from '../services/authService';
import LocationSelect from '../components/LocationSelect';

function parsePaidAmount(value) {
  const normalized = String(value ?? '').replace(/[,\s]/g, '').replace(/^(?:₹|rs\.?)/i, '').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export const SettingsPage = ({ onNavigate, onLogout, navState }) => {
  const { user, updateProfile, logout } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [profileImage, setProfileImage] = useState(user.profilePhoto || user.profile_photo_url || '');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const docInputRef = useRef(null);

  // Predefined allowed practice areas
  const PREDEFINED_PRACTICE_AREAS = [
    'Property Law', 'Family Law', 'Civil Law', 'Criminal Law', 'Corporate Law',
    'Labour Law', 'Constitutional Law', 'Tax Law', 'Intellectual Property', 'Immigration Law',
  ];

  const cleanPracticeAreas = (arr) => {
    if (!arr) return [];
    const raw = Array.isArray(arr) ? arr : String(arr).split(/[;,|]/);
    const cleaned = raw
      .map((a) => String(a || '').trim())
      .filter((a) =>
        a.length > 0 && a.length < 30 &&
        !a.toLowerCase().includes('experience') &&
        !a.toLowerCase().includes('year') &&
        !a.toLowerCase().includes('cases') &&
        !a.includes(',')
      );
    return [...new Set(
      cleaned.filter((a) => PREDEFINED_PRACTICE_AREAS.some((p) => p.toLowerCase() === a.toLowerCase()))
        .map((a) => PREDEFINED_PRACTICE_AREAS.find((p) => p.toLowerCase() === a.toLowerCase()) || a)
    )];
  };

  const initialPracticeAreas = cleanPracticeAreas(user.practiceAreas || user.practice_areas || []);
  const [practiceAreas, setPracticeAreas] = useState(initialPracticeAreas.length ? initialPracticeAreas : []);
  const [newPracticeArea, setNewPracticeArea] = useState('');
  const [customPracticeArea, setCustomPracticeArea] = useState('');
  const [isAddingPracticeArea, setIsAddingPracticeArea] = useState(false);
  const [activeTab, setActiveTab] = useState(navState?.activeTab || 'profile');

  useEffect(() => {
    if (navState?.activeTab) {
      setActiveTab(navState.activeTab);
    }
  }, [navState]);

  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    phone: user.phone,
    specialization: user.specialization,
    barEnrollmentYear: user.barEnrollmentYear || '',
    consultationFee: user.consultation_fee ?? user.consultationFee ?? 0,
    state: user.state || '',
    location: user.location || user.city || '',
    age: user.age || '',
    college: user.college || '',
    panCard: user.panCard || '',
    workplace: user.workplace || '',
    barCouncilNo: user.barCouncilNo || user.enrollment_number || '',
    barCouncilIdDocument: user.barCouncilIdDocument || '',
    address: user.address || '',
    workAddress: user.workAddress || '',
  });

  // Keep formData in sync if the user object updates (e.g. after AuthContext profile fetch)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        specialization: user.specialization || prev.specialization,
        barEnrollmentYear: user.barEnrollmentYear || prev.barEnrollmentYear,
        consultationFee: user.consultation_fee ?? user.consultationFee ?? prev.consultationFee,
        state: user.state || prev.state,
        location: user.location || user.city || prev.location,
        age: user.age || prev.age,
        college: user.college || prev.college,
        panCard: user.panCard || prev.panCard,
        workplace: user.workplace || prev.workplace,
        barCouncilNo: user.barCouncilNo || user.enrollment_number || prev.barCouncilNo,
        barCouncilIdDocument: user.barCouncilIdDocument || prev.barCouncilIdDocument,
        address: user.address || prev.address,
        workAddress: user.workAddress || prev.workAddress,
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        specialization: formData.specialization,
        barEnrollmentYear:
          formData.barEnrollmentYear !== '' && formData.barEnrollmentYear != null
            ? Number.parseInt(String(formData.barEnrollmentYear), 10)
            : undefined,
        city: formData.location,
        state: formData.state,
        // Sanitize before sending: deduplicate and strip dirty entries
        practice_areas: [...new Set(
          practiceAreas
            .map((a) => a.trim())
            .filter(
              (a) =>
                a.length > 0 &&
                a.length < 30 &&
                !a.toLowerCase().includes('experience') &&
                !a.toLowerCase().includes('year') &&
                !a.toLowerCase().includes('cases') &&
                !a.includes(',')
            )
        )],
        enrollment_number: formData.barCouncilNo,
        barCouncilIdDocument: formData.barCouncilIdDocument,
        age:
          formData.age !== '' && formData.age != null
            ? Number.parseInt(String(formData.age), 10)
            : undefined,
        college: formData.college,
        pan_card: formData.panCard,
        workplace: formData.workplace,
        address: formData.address,
        work_address: formData.workAddress,
        profile_photo_url: profileImage || undefined,
        profilePhoto: profileImage || undefined,
        consultation_fee:
          formData.consultationFee !== '' && formData.consultationFee != null
            ? parsePaidAmount(formData.consultationFee) ?? 0
            : undefined,
      };

      const persisted = await updateDashboardProfile(payload);
      const persistedFee =
          persisted?.consultation_fee != null
            ? persisted.consultation_fee
            : (formData.consultationFee !== '' && formData.consultationFee != null
                ? parsePaidAmount(formData.consultationFee) ?? 0
                : 0);

      const persistedName       = persisted?.full_name || formData.name;
      const persistedEmail      = persisted?.email || formData.email;
      const persistedPhone      = persisted?.phone || formData.phone;
      const persistedLocation   = persisted?.city || formData.location;
      const persistedState      = persisted?.state || formData.state;
      const persistedEnrolment  = persisted?.barEnrollmentYear ?? formData.barEnrollmentYear;

      // Do NOT spread raw formData — it uses camelCase keys that can corrupt the
      // auth user object and trigger the profileComplete guard incorrectly.
      updateProfile({
        name:              persistedName,
        email:             persistedEmail,
        phone:             persistedPhone,
        location:          persistedLocation,
        city:              persistedLocation,
        state:             persistedState,
        specialization:    formData.specialization,
        barEnrollmentYear: persistedEnrolment,
        consultation_fee:  persistedFee,
        practiceAreas,
        profilePhoto:      profileImage,
        profile_photo_url: profileImage,
        age:               formData.age,
        college:           formData.college,
        panCard:           formData.panCard,
        workplace:         formData.workplace,
        barCouncilNo:      formData.barCouncilNo,
        barCouncilIdDocument: formData.barCouncilIdDocument,
        address:           formData.address,
        workAddress:       formData.workAddress,
        profileComplete:   true,  // always true — backend controls verification, not frontend
      });

      // Sync local formData so repeated saves don’t revert consultation fee
      setFormData((prev) => ({
        ...prev,
        name:            persistedName,
        email:           persistedEmail,
        phone:           persistedPhone,
        location:        persistedLocation,
        state:           persistedState,
        barEnrollmentYear: persistedEnrolment,
        consultationFee: persistedFee,
      }));

      alert('Profile updated successfully.');
    } catch (err) {
      console.error('Profile update failed:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!password || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword({ currentPassword: password, newPassword });
      setPasswordSuccess('Password updated successfully. Redirecting to login…');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Invalidate the current session client-side: clears lawyerAuthToken + lawyerUser
      // from localStorage and resets React auth state. onLogout handles the redirect.
      setTimeout(() => {
        logout();
        onLogout();
      }, 2000);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to change password.';
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePhotoPick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = () => setProfileImage(String(reader.result || ''));
    reader.readAsDataURL(file);

    // Upload to Supabase Storage via backend
    setIsUploadingPhoto(true);
    setPhotoError('');
    try {
      const publicUrl = await uploadProfilePhoto(file);
      setProfileImage(publicUrl);           // replace base64 preview with real URL
      updateProfile({ profilePhoto: publicUrl, profile_photo_url: publicUrl });
      setShowPhotoModal(false);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setPhotoError('Failed to upload photo. Please try again.');
      setProfileImage(user.profilePhoto || user.profile_photo_url || '');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setIsRemovingPhoto(true);
    setPhotoError('');
    try {
      await deleteProfilePhoto();
      
      // Clear image
      setProfileImage('');
      
      // Update auth context so other components re-render correctly
      updateProfile({ profilePhoto: null, profile_photo_url: null });
      
      setShowRemoveConfirm(false);
      setShowPhotoModal(false);
    } catch (err) {
      console.error('Photo removal failed:', err);
      const msg = err.message || 'Failed to remove photo. Please try again.';
      setPhotoError(msg);
      alert(msg);
      setShowRemoveConfirm(false);
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  const handleDocChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Document size must be less than 5MB");
      return;
    }

    // Validate type
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert("Only JPG, PNG and PDF files are allowed");
      return;
    }

    setIsUploadingDoc(true);
    try {
      const publicUrl = await uploadVerificationDocument(file);
      setFormData(prev => ({ ...prev, barCouncilIdDocument: publicUrl }));
      alert('Document uploaded successfully. Please save changes.');
    } catch (err) {
      console.error('Doc upload failed:', err);
      alert(err.message || 'Failed to upload document');
    } finally {
      setIsUploadingDoc(false);
    }
    // clear input
    if (docInputRef.current) docInputRef.current.value = '';
  };

  const handleRemoveDoc = () => {
    setFormData(prev => ({ ...prev, barCouncilIdDocument: '' }));
  };

  const handleAddPracticeArea = () => {
    let finalArea = '';
    
    if (newPracticeArea === 'Others') {
      const cleanedCustom = customPracticeArea.trim();
      if (!cleanedCustom) {
        alert('Please enter a custom practice area.');
        return;
      }
      finalArea = cleanedCustom;
    } else {
      const cleaned = newPracticeArea.trim();
      if (!cleaned) return;
      
      const matched = PREDEFINED_PRACTICE_AREAS.find(
        (p) => p.toLowerCase() === cleaned.toLowerCase()
      );
      if (!matched) {
        alert(`"${cleaned}" is not a recognized practice area. Please choose from the list or select "Others".`);
        return;
      }
      finalArea = matched;
    }

    const alreadyExists = practiceAreas.some(
      (area) => area.toLowerCase() === finalArea.toLowerCase()
    );
    if (alreadyExists) {
      alert('Practice area already added.');
      return;
    }

    setPracticeAreas((prev) => [...prev, finalArea]);
    setNewPracticeArea('');
    setCustomPracticeArea('');
    setIsAddingPracticeArea(false);
  };

  // Derive a display-safe role label; never show raw 'lawyer' (sidebar already shows the portal name)
  const roleLabel = String(user?.role || user?.profession || 'Advocate').trim();
  const displayRole = roleLabel.toLowerCase() === 'lawyer' ? 'Advocate' : roleLabel;

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'general', label: 'General' },
    { id: 'security', label: 'Security' },
  ];

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Manage your account and preferences"
        showBack={false}
      />

      {/* Sub-sections under Settings */}
      <div className="mb-6 rounded-xl bg-[#071b33] px-3 py-2 shadow-[0_10px_24px_rgba(2,6,23,0.18)] overflow-hidden">
        <div className="flex gap-2 overflow-x-auto pb-1 -mb-1 px-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{ minHeight: '40px' }}
              className={`px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm whitespace-nowrap shrink-0 transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-[#071b33]'
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card className="p-0">
          {!user.verified && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 rounded-t-xl flex items-start gap-3">
              <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="text-amber-800 font-semibold">Verification Pending</h4>
                <p className="text-sm text-amber-700 mt-1">
                  {(() => {
                    const missing = [];
                    if (!user.barCouncilIdDocument) missing.push("Bar Council ID");
                    if (!user.enrollment_number && !user.barCouncilNo) missing.push("Bar Council Enrollment Number");
                    if (!user.panCard && !user.pan_card) missing.push("PAN Card Number");
                    
                    if (missing.length === 0) return "Your profile is under review by the admin.";
                    if (missing.length === 1) return `Please provide your ${missing[0]} to become a Verified Lawyer.`;
                    
                    const last = missing.pop();
                    return `Please provide your ${missing.join(', ')} and ${last} to become a Verified Lawyer.`;
                  })()}
                </p>
              </div>
            </div>
          )}
          <div className="p-6">
            <h3 className="text-3xl font-bold mb-6">Profile Information</h3>
            <div className="flex items-center gap-4 mb-6">
              <button
                type="button"
                onClick={() => setShowPhotoModal(true)}
                className="rounded-full focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all"
                aria-label="Manage profile photo"
                title="Manage profile photo"
              >
                <Avatar className="w-16 h-16 min-w-[4rem] min-h-[4rem] cursor-pointer relative shadow-sm border border-slate-200 hover:opacity-90 transition-opacity bg-white">
                  <AvatarImage src={profileImage} alt={formData.name} />
                  {!profileImage && <AvatarFallback className="text-2xl bg-blue-50 text-blue-700 font-bold">{formData.name?.[0] || 'A'}</AvatarFallback>}
                </Avatar>
              </button>
              <div>
                <p className="text-3xl font-bold">{user.name}</p>
                {/* Role + Verified — shown ONCE here in the header card */}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-sm font-medium text-slate-500">{displayRole}</span>
                  {user.verified ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 font-semibold">
                      <Check size={12} className="mr-1 text-blue-500" /> Verified Lawyer
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 font-semibold">
                      <AlertCircle size={12} className="mr-1 text-amber-500" /> Verification Pending
                    </span>
                  )}
                </div>
                {/* Enrolment Year — shown ONCE */}
                {formData.barEnrollmentYear != null && formData.barEnrollmentYear !== '' && (
                  <p className="text-sm text-slate-500 mt-1">
                    Enrolled: {formData.barEnrollmentYear}
                  </p>
                )}
                {isUploadingPhoto && (
                  <p className="text-xs text-blue-600 mt-1 animate-pulse">⏳ Uploading photo...</p>
                )}
                {photoError && (
                  <p className="text-xs text-red-500 mt-1">{photoError}</p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-xl font-bold mb-3">Practice Areas</h4>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {practiceAreas.map((area) => (
                  <span
                    key={area}
                    title={area}
                    className="inline-flex items-center justify-between gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs sm:text-sm font-semibold max-w-[200px] sm:max-w-[280px] transition-colors"
                  >
                    <span className="truncate">{area}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${area}`}
                      onClick={() => setPracticeAreas((prev) => prev.filter((a) => a !== area))}
                      style={{ minWidth: '32px', minHeight: '32px' }}
                      className="inline-flex items-center justify-center text-blue-500 hover:text-red-500 hover:bg-blue-100 rounded-full font-bold transition-colors shrink-0 -mr-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </span>
                ))}
                {!isAddingPracticeArea && (
                  <button
                    type="button"
                    onClick={() => setIsAddingPracticeArea(true)}
                    style={{ minHeight: '36px' }}
                    className="px-4 py-1.5 rounded-full border border-slate-300 text-slate-600 text-xs sm:text-sm hover:border-blue-400 hover:text-blue-600 font-semibold transition-colors"
                  >
                    + Add
                  </button>
                )}
              </div>

              {isAddingPracticeArea && (
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Dropdown restricted to predefined values not yet added */}
                  <select
                    value={newPracticeArea}
                    onChange={(e) => setNewPracticeArea(e.target.value)}
                    className="flex-1 h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  >
                    <option value="">-- Select a practice area --</option>
                    {PREDEFINED_PRACTICE_AREAS
                      .filter((p) => !practiceAreas.includes(p))
                      .map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    <option value="Others">Others</option>
                  </select>
                  
                  {newPracticeArea === 'Others' && (
                    <input
                      type="text"
                      placeholder="Enter practice area"
                      value={customPracticeArea}
                      onChange={(e) => setCustomPracticeArea(e.target.value)}
                      className="flex-1 h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleAddPracticeArea}
                    className="px-4 py-2 rounded-xl bg-[#071b33] text-white font-semibold"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <Input label="Full Name" name="name" value={formData.name} onChange={handleInputChange} />
              <Input label="Email" name="email" value={formData.email} onChange={handleInputChange} />
              <Input label="Phone" name="phone" value={formData.phone} onChange={handleInputChange} />
              {/* State + City — searchable dropdowns */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Location (State / District)</label>
                <LocationSelect
                  state={formData.state}
                  city={formData.location}
                  onStateChange={(val) => setFormData(prev => ({ ...prev, state: val, location: '' }))}
                  onCityChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
                  labelState="State"
                  labelCity="District"
                />
              </div>
              <Input label="Age" name="age" value={formData.age} onChange={handleInputChange} />
              <Input label="College/Institution" name="college" value={formData.college} onChange={handleInputChange} />
              <Input label="Speciality" name="specialization" value={formData.specialization} onChange={handleInputChange} />
              <Input label={<>Bar Enrolment Year (e.g. 2023) <span className="text-red-500">*</span></>} name="barEnrollmentYear" type="number" min={1950} max={new Date().getFullYear()} value={formData.barEnrollmentYear} onChange={handleInputChange} />
              <Input
                label="Consultation Fee (INR)"
                name="consultationFee"
                type="number"
                min={0}
                step={100}
                value={formData.consultationFee ?? 0}
                onChange={handleInputChange}
              />
              <Input label="Workplace" name="workplace" value={formData.workplace} onChange={handleInputChange} />
              
              <div className="pt-4 border-t border-slate-100">
                <h4 className="text-lg font-semibold text-slate-800 mb-4">Verification Documents</h4>
                <div className="space-y-4">
                  <Input label={<>Bar Council Enrollment No. <span className="text-red-500">*</span></>} name="barCouncilNo" value={formData.barCouncilNo} onChange={handleInputChange} />
                  
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Bar Council ID <span className="text-red-500">*</span></label>
                    <div className="p-4 border border-slate-300 rounded-lg bg-slate-50">
                      {formData.barCouncilIdDocument ? (
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-700">Document Uploaded</span>
                            <a href={formData.barCouncilIdDocument} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                              View Current File
                            </a>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => docInputRef.current?.click()}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                              disabled={isUploadingDoc}
                            >
                              {isUploadingDoc ? 'Uploading...' : 'Replace'}
                            </button>
                            <button
                              type="button"
                              onClick={handleRemoveDoc}
                              className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
                              disabled={isUploadingDoc}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-start gap-2">
                          <span className="text-sm text-slate-500">Upload your Bar Council ID (JPG, PNG, PDF up to 5MB)</span>
                          <button
                            type="button"
                            onClick={() => docInputRef.current?.click()}
                            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            disabled={isUploadingDoc}
                          >
                            {isUploadingDoc ? 'Uploading...' : 'Upload Document'}
                          </button>
                        </div>
                      )}
                      <input
                        ref={docInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="hidden"
                        onChange={handleDocChange}
                      />
                    </div>
                  </div>

                  <Input label={<>PAN Card <span className="text-red-500">*</span></>} name="panCard" value={formData.panCard} onChange={handleInputChange} />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <Input label="Address" name="address" value={formData.address} onChange={handleInputChange} />
                <Input label="Work Address" name="workAddress" value={formData.workAddress} onChange={handleInputChange} />
              </div>
              <Button onClick={handleSaveProfile} loading={isSaving}>Save Changes</Button>
            </div>
          </div>
        </Card>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <Card className="p-0">
          <div className="p-6">
            <h3 className="text-3xl font-bold mb-6">Notifications</h3>
            <div className="space-y-4">
              {['New booking requests', 'Payment updates', 'AI case insights', 'Consultation reminders'].map((n, idx) => (
                <div key={n} className="flex items-center justify-between py-3 border-b border-slate-100">
                  <p className="font-medium text-slate-800">{n}</p>
                  <Switch defaultChecked={idx < 3} />
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <h4 className="text-xl font-bold mb-4">Professional Plan</h4>
              <Card className="p-0 bg-[#071b33] text-white">
                <div className="p-6">
                  <h3 className="text-2xl font-bold">Professional Plan</h3>
                  <p className="text-slate-300 mt-1">Active · Renews Apr 5, 2026</p>
                  <div className="mt-4">
                    <button className="px-4 py-2 text-sm rounded-lg bg-white text-[#071b33] font-semibold">Manage</button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Card>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <Card className="p-0">
          <div className="p-6">
            <h3 className="text-3xl font-bold mb-6">Security Settings</h3>

            <div className="mb-8">
              <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Lock size={20} />
                Change Password
              </h4>
              <div className="space-y-3 max-w-md">
                <div>
                  <label className="block text-sm font-semibold text-slate-800 mb-1.5">Current Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-800 mb-1.5">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-800 mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                {passwordError && (
                  <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {passwordError}
                  </p>
                )}
                {passwordSuccess && (
                  <p className="text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    {passwordSuccess}
                  </p>
                )}
                <Button onClick={handleChangePassword} loading={passwordLoading} className="text-sm">Update Password</Button>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h4 className="text-xl font-bold mb-4">Account Actions</h4>
              <button
                onClick={onLogout}
                className="px-4 py-2 text-sm rounded-lg bg-[#071b33] text-white font-semibold hover:bg-slate-900 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </Card>
      )}

      {showPhotoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
          onClick={() => { if (!isUploadingPhoto) setShowPhotoModal(false); }}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 flex flex-col items-center text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Profile Photo</h3>
              
              <Avatar className="w-32 h-32 mb-6 ring-4 ring-slate-50 shadow-md bg-white">
                <AvatarImage src={profileImage} alt={formData.name} />
                {!profileImage && <AvatarFallback className="text-5xl bg-blue-50 text-blue-700 font-bold">{formData.name?.[0] || 'A'}</AvatarFallback>}
              </Avatar>

              <div className="w-full space-y-3">
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 text-base font-semibold"
                  onClick={handlePhotoPick}
                  loading={isUploadingPhoto}
                  disabled={isUploadingPhoto || isRemovingPhoto}
                >
                  <Camera size={18} className="mr-2" /> Upload Photo
                </Button>

                {profileImage && (
                  <Button
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl h-11 text-base font-semibold"
                    onClick={() => { setShowRemoveConfirm(true); setShowPhotoModal(false); }}
                    disabled={isUploadingPhoto || isRemovingPhoto}
                  >
                    <Trash2 size={18} className="mr-2" /> Remove Photo
                  </Button>
                )}

                <Button
                  variant="ghost"
                  className="w-full text-slate-500 hover:bg-slate-100 rounded-xl h-11 text-base font-medium"
                  onClick={() => setShowPhotoModal(false)}
                  disabled={isUploadingPhoto || isRemovingPhoto}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-500 mb-4 mx-auto">
                <Trash2 size={24} />
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 mb-2">Remove Profile Photo?</h3>
              <p className="text-sm text-center text-slate-500">
                Are you sure you want to remove your profile photo? It will be permanently deleted and replaced with a default initial avatar.
              </p>
            </div>
            <div className="p-4 bg-slate-50 flex items-center justify-end gap-3 border-t">
              <Button
                variant="outline"
                className="bg-white text-slate-700 hover:bg-slate-100"
                onClick={() => setShowRemoveConfirm(false)}
                disabled={isRemovingPhoto}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white border-transparent"
                onClick={handleRemovePhoto}
                loading={isRemovingPhoto}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
